class AcOverlaySystem extends HTMLElement {
  #abortController;
  #activePanel = null;
  #opener = null;
  #closeTimer = 0;
  #openTimer = 0;
  #openGeneration = 0;
  #closingPanel = null;
  #finishClosing = null;
  #motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  #reduced = () => document.body.dataset.acMotion === 'reduced' || this.#motionQuery.matches;
  #syncMotion = () => {
    if (!this.#reduced()) return;
    window.clearTimeout(this.#openTimer);
    window.clearTimeout(this.#closeTimer);
    if (this.#activePanel) this.#activePanel.dataset.motionSettled = 'true';
    this.#finishClosing?.();
  };

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;
    const motionObserver = new MutationObserver(this.#syncMotion);
    motionObserver.observe(document.body, { attributes: true, attributeFilter: ['data-ac-motion'] });
    this.#motionQuery.addEventListener('change', this.#syncMotion, { signal });
    signal.addEventListener('abort', () => motionObserver.disconnect(), { once: true });
    document.addEventListener('click', this.#handleClick, { signal });
    document.addEventListener('ac:navigation:opened', this.#handleNavigationOpen, { signal });
    document.addEventListener('shopify:section:load', () => this.#syncTriggers(), { signal });
    this.querySelectorAll('[data-ac-overlay]').forEach((panel) => {
      panel.dataset.state = 'closed';
      panel.setAttribute('aria-hidden', 'true');
      panel.inert = true;
    });
    this.#syncTriggers();
    this.dataset.ready = 'true';
    document.dispatchEvent(new CustomEvent('ac:overlay-readiness'));
  }

  disconnectedCallback() {
    delete this.dataset.ready;
    document.dispatchEvent(new CustomEvent('ac:overlay-readiness'));
    this.#abortController?.abort();
    window.clearTimeout(this.#closeTimer);
    window.clearTimeout(this.#openTimer);
    this.#openGeneration += 1;
    this.#finishClose();
  }

  open(name, trigger) {
    const panel = this.querySelector(`[data-ac-overlay="${CSS.escape(name)}"]`);
    if (!panel) return;
    if (this.#activePanel === panel) {
      if (trigger && trigger !== this.#opener) {
        this.#opener = trigger;
        document.dispatchEvent(new CustomEvent('ac:overlay-open', { detail: { name, panel, trigger } }));
      } else {
        this.close();
      }
      return;
    }

    window.clearTimeout(this.#closeTimer);
    window.clearTimeout(this.#openTimer);
    const generation = ++this.#openGeneration;
    this.#opener = trigger || document.activeElement;
    window.AcLayerManager?.acquire('overlay', {
      opener: this.#opener,
      getFocusRoots: () => [document.querySelector('[data-ac-ref-header-system]'), this.#activePanel],
      onEscape: () => this.close(),
    });
    if (this.#closingPanel) this.#hidePanel(this.#closingPanel);
    this.#closingPanel = null;
    this.#finishClosing = null;
    if (this.#activePanel) {
      const previousPanel = this.#activePanel;
      this.#hidePanel(previousPanel);
      document.dispatchEvent(new CustomEvent('ac:overlay-close', { detail: { name: previousPanel.dataset.acOverlay, panel: previousPanel, switched: true } }));
    }
    this.#activePanel = panel;
    delete panel.dataset.motionSettled;
    panel.inert = false;
    panel.setAttribute('aria-hidden', 'false');
    panel.dataset.state = 'opening';
    this.classList.add('is-active');

    this.#syncHeader(name);
    document.dispatchEvent(new CustomEvent('ac:overlay-open', { detail: { name, panel, trigger } }));
    document.dispatchEvent(new CustomEvent('ac:overlay-change', { detail: { name } }));

    requestAnimationFrame(() => {
      if (this.#activePanel !== panel || generation !== this.#openGeneration) return;
      panel.dataset.state = 'open';
      this.#openTimer = window.setTimeout(() => {
        if (this.#activePanel === panel && generation === this.#openGeneration) panel.dataset.motionSettled = 'true';
      }, this.#motionDuration(panel));
      const preferred = name === 'search' ? panel.querySelector('[data-ac-search-input]') : this.#focusableElements(panel)[0];
      preferred?.focus({ preventScroll: true });
    });
  }

  close({ restoreFocus = true, immediate = false, preservePageInert = false } = {}) {
    const panel = this.#activePanel;
    if (!panel) return;
    window.clearTimeout(this.#openTimer);
    this.#openGeneration += 1;
    delete panel.dataset.motionSettled;
    this.#activePanel = null;
    this.#opener = null;
    panel.dataset.state = immediate ? 'closed' : 'closing';
    panel.setAttribute('aria-hidden', 'true');
    panel.inert = true;
    this.classList.remove('is-active');
    this.#closingPanel = panel;
    this.#syncHeader(null);
    document.dispatchEvent(new CustomEvent('ac:overlay-close', { detail: { name: panel.dataset.acOverlay, panel } }));
    document.dispatchEvent(new CustomEvent('ac:overlay-change', { detail: { name: null } }));
    const finish = () => {
      this.#finishClosing = null;
      this.#hidePanel(panel);
      if (this.#closingPanel === panel) this.#closingPanel = null;
      window.AcLayerManager?.release('overlay', { restoreFocus: restoreFocus && !preservePageInert });
    };

    this.#finishClosing = finish;
    if (immediate) finish();
    else this.#closeTimer = window.setTimeout(finish, this.#motionDuration(panel));
  }

  announce(message) {
    const live = this.querySelector('[data-ac-overlay-live]');
    if (!live) return;
    live.textContent = '';
    requestAnimationFrame(() => { live.textContent = message; });
  }

  #handleClick = (event) => {
    const trigger = event.target.closest?.('[data-ac-overlay-open], [data-ac-ref-open]');
    const overlayName = trigger?.dataset.acOverlayOpen || trigger?.dataset.acRefOpen;
    if (trigger && overlayName && this.querySelector(`[data-ac-overlay="${CSS.escape(overlayName)}"]`)) {
      event.preventDefault();
      this.open(overlayName, trigger);
      return;
    }

    if (event.target.closest('[data-ac-overlay-close], [data-ac-overlay-scrim]')) {
      event.preventDefault();
      this.close();
    }
  };

  #handleNavigationOpen = () => {
    if (this.#activePanel) this.close({ restoreFocus: false, immediate: true, preservePageInert: true });
  };

  #hidePanel(panel) {
    delete panel.dataset.motionSettled;
    panel.dataset.state = 'closed';
    panel.setAttribute('aria-hidden', 'true');
    panel.inert = true;
  }

  #finishClose() {
    if (this.#activePanel) this.#hidePanel(this.#activePanel);
    if (this.#closingPanel) this.#hidePanel(this.#closingPanel);
    this.#activePanel = null;
    this.#closingPanel = null;
    this.#finishClosing = null;
    this.#opener = null;
    this.classList.remove('is-active');
    window.AcLayerManager?.release('overlay', { restoreFocus: false });
    this.#syncHeader(null);
  }

  #syncHeader(name) {
    const header = document.querySelector('[data-ac-ref-header-system]');
    header?.classList.toggle('is-overlay-open', Boolean(name));
    header?.classList.toggle('is-favorites-open', name === 'wishlist');
    this.#syncTriggers(name);
  }

  #syncTriggers = (name = this.#activePanel?.dataset.acOverlay || null) => {
    document.querySelectorAll('[data-ac-ref-open], [data-ac-overlay-open]').forEach((trigger) => {
      const triggerName = trigger.dataset.acOverlayOpen || trigger.dataset.acRefOpen;
      if (trigger.hasAttribute('aria-haspopup')) trigger.setAttribute('aria-expanded', String(triggerName === name));
    });
  };

  #focusableElements(root) {
    if (!root) return [];
    return [...root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hidden && !element.closest('[inert]') && element.getClientRects().length);
  }

  #motionDuration(panel) {
    if (this.#reduced()) return 0;
    const value = getComputedStyle(panel).getPropertyValue('--ac-overlay-motion-duration').trim();
    if (value.endsWith('ms')) return Number.parseFloat(value) || 0;
    if (value.endsWith('s')) return (Number.parseFloat(value) || 0) * 1000;
    return 0;
  }
}

if (!customElements.get('ac-overlay-system')) customElements.define('ac-overlay-system', AcOverlaySystem);
